import React, { FC, PropsWithChildren, useEffect, useState } from "react";

import { Box, MenuList, Stack, Typography } from "@mui/material";

import { IconButton, Menu } from "@ui/interactive";
import { Link } from "@ui/interactive";
import { handleCloseOnKeyDown } from "@ui/interactive";
import { TIconButton } from "@ui/interactive/IconButton/IconButton";

import choir from "@assets/jpg/Footer/audio-choir-sanctum.jpg";
import chant from "@assets/webp/Footer/chant-sanctum.webp";
import piano from "@assets/webp/Footer/piano-sanctum.webp";
import soundtrack from "@assets/webp/Footer/soundtrack-sanctum.webp";

import { bindTrigger, usePopupState } from "material-ui-popup-state/hooks";

import { LinkType } from "./types";

type FooterIconMenuProps = {
  links: LinkType[];
  title: string;
  isLight?: boolean;
};

const linkSx = {
  fontFamily: "Raleway",
  fontSize: "13px",
  fontWeight: 400,
  color: "#e0d2c1",
  textDecoration: "none",
  padding: "8px 10px",
  borderRadius: "8px",
  display: "flex",
  alignItems: "center",
  gap: "10px",
  cursor: "pointer",
  transition: "all 0.2s ease",
  "&:hover": {
    background: "rgba(255, 255, 255, 0.06)",
    color: "#fff",
  },
};

export const FooterIconMenu: FC<PropsWithChildren & FooterIconMenuProps & TIconButton> = ({
  links,
  isFooter = true,
  title,
  children,
  isLight: _isLight,
  ...restProps
}) => {
  const popupState = usePopupState({ variant: "popper", popupId: "song-menu-popover" });

  const imgs: Record<string, string> = {
    soundtrack,
    piano,
    choir,
    chant,
  };

  const [isOpen, setIsOpen] = useState(false);
  const triggers = bindTrigger(popupState);

  useEffect(() => {
    setIsOpen(popupState.isOpen);
  }, [popupState]);

  return (
    <>
      <IconButton
        {...triggers}
        onClick={(e) => {
          if (isOpen) {
            return popupState.close();
          }
          triggers.onClick(e);
        }}
        isFooter={isFooter}
        {...restProps}
      >
        {children}
      </IconButton>

      <Menu
        popupState={popupState}
        offset={[0, 12]}
        placement="top-start"
        paperProps={{
          elevation: 0,
          width: "220px",
          sx: {
            width: "220px",
            overflow: "visible",
            filter: "drop-shadow(0px 14px 44px rgba(0,0,0,0.4))",
            border: "1px solid rgba(235,194,142,0.18)",
            borderRadius: "12px",
            backgroundClip: "unset",
            backgroundColor: "transparent",
            backgroundImage:
              "linear-gradient(180deg, rgb(153, 112, 75) 0%, rgb(99, 74, 54) 50%, rgb(111, 83, 60) 100%)",
          },
        }}
      >
        <MenuList
          autoFocus={popupState.isOpen}
          sx={{ padding: 0 }}
          onKeyDown={(event) => handleCloseOnKeyDown(event, popupState)}
        >
          <Stack sx={{ padding: "16px" }}>
            {/* Title */}
            <Typography
              sx={{
                fontFamily: "Raleway",
                fontSize: "11px",
                fontWeight: 600,
                color: "#ffdca8",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                paddingBottom: "8px",
                paddingLeft: "10px",
              }}
            >
              {title}
            </Typography>

            {/* Divider */}
            <Box sx={{ height: "1px", background: "rgba(235, 194, 142, 0.18)", marginBottom: "8px" }} />

            {/* Links */}
            <Stack gap="2px">
              {links.map(({ title: linkTitle, src, type }) => (
                <Link key={src} href={src} target="_blank" style={{ textDecoration: "none" }}>
                  <Box sx={linkSx}>
                    <Box
                      sx={{
                        width: "28px",
                        height: "28px",
                        borderRadius: "6px",
                        overflow: "hidden",
                        flexShrink: 0,
                      }}
                    >
                      <img
                        src={imgs[type]}
                        alt={linkTitle}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    </Box>
                    <Typography
                      sx={{
                        fontFamily: "Raleway",
                        fontSize: "13px",
                        fontWeight: 400,
                        color: "inherit",
                      }}
                    >
                      {linkTitle}
                    </Typography>
                  </Box>
                </Link>
              ))}
            </Stack>
          </Stack>
        </MenuList>
      </Menu>
    </>
  );
};
